import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { BusinessDoc } from '../../shared/api/converters.js';
import { replaceCachedBusiness } from '../../shared/api/businessCache.js';
import { fetchBusinessFromServer } from '../../shared/api/serverRead.js';
import { isSubscribed } from '../../shared/lib/subscription.js';
import { useSyncSubscription } from './hooks.js';

/**
 * Back from Stripe Checkout with `?session_id=…`: the wizard's Activate step
 * (a business in setup) or Settings › Billing (everyone else), as decided by
 * `createCheckoutSession`.
 *
 * The redirect beats the webhook more often than not, so the subscription is
 * read straight from Stripe (`syncSubscription`) while the live listener
 * races it from the other side. The sync runs ONCE, never on a poll: the
 * webhook is the backstop, and retrying forever would hammer Stripe for an
 * owner who has already paid. Until the live document says paid, the caller
 * shows "confirming" — never a second offer to subscribe, never a suggestion
 * that the payment failed.
 */
export function useCheckoutReturn(business: BusinessDoc) {
  const [params, setParams] = useSearchParams();
  const sessionId = params.get('session_id');
  const subscribed = isSubscribed(business);
  const sync = useSyncSubscription(business.id, sessionId ?? undefined);
  const qc = useQueryClient();

  // The listener is the usual way the paid state arrives, but it can come back
  // silently dead after the tab sat behind Stripe's page — so once the sync
  // has written, read the business from the server as well.
  const confirm = () =>
    sync.mutate(undefined, {
      onSettled: async () => {
        const fresh = await fetchBusinessFromServer(business.id).catch(() => null);
        if (fresh) replaceCachedBusiness(qc, business.ownerUid, fresh);
      },
    });
  const synced = useRef(false);
  useEffect(() => {
    if (!sessionId || subscribed || synced.current) return;
    synced.current = true;
    confirm();
    // `confirm` is rebuilt every render; the ref makes this fire once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, subscribed]);

  // Paid and visible: the session id has done its job — drop it so a reload
  // does not ask Stripe again.
  useEffect(() => {
    if (!sessionId || !subscribed) return;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('session_id');
        return next;
      },
      { replace: true },
    );
  }, [sessionId, subscribed, setParams]);

  const confirming = !!sessionId && !subscribed;
  return {
    /** Paid at Stripe, not yet visible here. */
    confirming,
    /** The one sync came back without an active subscription (or failed). */
    stalled: confirming && (sync.isError || (sync.isSuccess && sync.data?.status !== 'active' && sync.data?.status !== 'trialing')),
    checking: sync.isPending,
    checkAgain: confirm,
    /** Pressed back in Stripe: nothing was charged. */
    canceled: params.get('canceled') === '1',
  };
}
